import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ModalBottomSheetRef } from '@fc-shared/ui/modal-bottom-sheet/modal-bottom-sheet-ref';
import { MODAL_BOTTOM_SHEET_DATA } from '@fc-shared/ui/modal-bottom-sheet/modal-bottom-sheet.service';
import { NgButtonComponent } from '@fc-shared/ui/buttons/ng-button';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { AttachmentModel } from '@fc-vehicles/service/attachment-model';
import moment from 'moment-timezone';
import { DATE_TIME_FORMATS } from '@fc-shared/utils/date-time-formats';

export interface MediaViewerSheetData {
  file: AttachmentModel;
}

@Component({
  selector: 'fc-media-viewer-bottomsheet',
  template: `
    <div class="flex flex-col">
      <div
        class="flex flex-row items-center justify-between border-b border-grey-100 px-24 py-16"
      >
        <div class="text-lg font-medium">Details</div>
        <ng-button
          variant="icon"
          icon="icon-close"
          (click)="sheetRef.dismiss()"
        ></ng-button>
      </div>

      <div class="flex-1 overflow-auto p-20">
        <div class="flex flex-col gap-16">
          @if (file?.name) {
            <div class="flex flex-row items-center gap-12">
              <fc-icon
                icon="icon-image"
                color="secondary400"
                [size]="20"
              ></fc-icon>
              <span class="truncate">{{ file?.name }}</span>
            </div>
          }

          @if (getFormattedDate()) {
            <div class="flex flex-row items-center gap-12">
              <fc-icon
                icon="icon-calendar_month"
                color="secondary400"
                [size]="20"
              ></fc-icon>
              <span class="truncate">{{ getFormattedDate() }}</span>
            </div>
          }

          @if (file?.exif?.camera?.make) {
            <div class="flex flex-row items-center gap-12">
              <fc-icon
                icon="icon-info-circle"
                color="secondary400"
                [size]="20"
              ></fc-icon>
              <span class="truncate">{{ file?.exif?.camera?.make }}</span>
            </div>
          }

          @if (file?.uploadedBy) {
            <div class="flex flex-row items-center gap-12">
              <fc-icon
                icon="icon-person"
                color="secondary400"
                [size]="20"
              ></fc-icon>
              <span class="truncate">{{ file?.uploadedBy }}</span>
            </div>
          }
        </div>
      </div>
    </div>
  `,
  imports: [NgButtonComponent, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaViewerBottomsheetComponent {
  protected readonly sheetRef = inject(ModalBottomSheetRef);
  readonly file = inject<MediaViewerSheetData>(MODAL_BOTTOM_SHEET_DATA).file;

  getFormattedDate(): string | null {
    if (!this.file) return null;

    const dateString =
      this.file.exif?.timestamp || this.file.created || this.file.modified;
    if (!dateString) return null;

    return moment(dateString).format(DATE_TIME_FORMATS.displayTimestamp);
  }
}
